import { Link } from "react-router-dom";
import { ScrollText, ShieldAlert, FileSignature, RefreshCw } from "lucide-react";
import useSeo from "../hooks/useSeo";

const SECTIONS = [
  {
    id: "introduccion",
    n: "1",
    icon: ScrollText,
    title: "Introducción",
    body: (
      <>
        <p>
          RivoNes (en adelante, <strong>“RivoNes”</strong>, <strong>“la Plataforma”</strong>,{" "}
          <strong>“nosotros”</strong> o <strong>“nuestro”</strong>) es una plataforma digital de
          intermediación tecnológica que conecta a propietarios de vehículos (en adelante, los{" "}
          <strong>“Anfitriones”</strong>) con usuarios que desean rentar dichos vehículos (en
          adelante, los <strong>“Usuarios”</strong> o <strong>“Conductores”</strong>).
        </p>
        <p>
          La plataforma RivoNes está disponible a través de su sitio web, aplicaciones móviles y
          cualquier otro medio digital asociado (en conjunto, los <strong>“Servicios”</strong>).
        </p>
        <p>
          Al acceder, registrarse o utilizar los Servicios, incluyendo la comunicación con otros
          usuarios dentro de la Plataforma, usted acepta cumplir y quedar legalmente obligado por
          los presentes Términos y Condiciones (los <strong>“Términos”</strong>),
          independientemente de si se registra formalmente como usuario.
        </p>
        <p>
          Estos Términos regulan el acceso y uso de los Servicios y constituyen un acuerdo legal
          vinculante entre usted y RivoNes.
        </p>
      </>
    ),
  },
  {
    id: "naturaleza",
    n: "2",
    icon: ShieldAlert,
    title: "Naturaleza de la Plataforma",
    body: (
      <>
        <p>RivoNes actúa exclusivamente como un intermediario tecnológico entre:</p>
        <ul>
          <li>Los propietarios de vehículos (“Anfitriones”), y</li>
          <li>Los usuarios que reservan vehículos (“Conductores”).</li>
        </ul>
        <div className="mt-5 p-5 rounded-2xl border border-amber-200 bg-amber-50">
          <p className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" />
            IMPORTANTE
          </p>
          <p className="text-amber-900 mb-2">RivoNes:</p>
          <ul className="text-amber-900">
            <li>No es propietario de los vehículos</li>
            <li>No actúa como arrendador directo</li>
            <li>No garantiza la condición, seguridad o legalidad de los vehículos</li>
            <li>No es responsable de accidentes, daños, robos o disputas entre usuarios</li>
          </ul>
        </div>
        <p className="mt-4">
          Toda relación de renta se celebra directamente entre el Anfitrión y el Conductor bajo su
          propia responsabilidad.
        </p>
      </>
    ),
  },
  {
    id: "acuerdo",
    n: "3",
    icon: FileSignature,
    title: "El Acuerdo",
    body: (
      <>
        <p>Los presentes Términos, junto con:</p>
        <ul>
          <li>Política de cancelación</li>
          <li>Política de reembolsos</li>
          <li>Política de uso del vehículo</li>
          <li>Política de seguros y daños</li>
          <li>Aviso de privacidad</li>
          <li>Cualquier otra política publicada en la Plataforma</li>
        </ul>
        <p>
          (en conjunto, las <strong>“Políticas”</strong>), constituyen el acuerdo total (el{" "}
          <strong>“Acuerdo”</strong>) entre usted y RivoNes.
        </p>
        <p>
          Adicionalmente, cada reserva generará un <strong>Contrato de Renta de Vehículo</strong>{" "}
          entre el Anfitrión y el Conductor, disponible dentro de la Plataforma como comprobante de
          la transacción.
        </p>
      </>
    ),
  },
  {
    id: "modificaciones",
    n: "4",
    icon: RefreshCw,
    title: "Modificaciones",
    body: (
      <>
        <p>
          RivoNes se reserva el derecho de modificar, en cualquier momento y a su entera discreción:
        </p>
        <ul>
          <li>Los Servicios</li>
          <li>Los presentes Términos</li>
          <li>Las Políticas</li>
        </ul>
        <p>
          Cualquier modificación será publicada en la Plataforma con su fecha de actualización
          correspondiente.
        </p>
        <p>
          El uso continuo de los Servicios después de dichas modificaciones constituye la aceptación
          de los nuevos Términos.
        </p>
        <p>
          Si usted no está de acuerdo con las modificaciones, deberá dejar de utilizar los Servicios
          y cancelar su cuenta dentro de un plazo máximo de <strong>30 días</strong>.
        </p>
      </>
    ),
  },
];

export default function TerminosPage() {
  useSeo({
    title: "Términos y Condiciones · Rivones",
    description:
      "Términos y Condiciones de uso de la plataforma RivoNes: intermediación tecnológica entre anfitriones y conductores para renta de autos en México.",
    keywords:
      "términos y condiciones rivones, términos rivones, condiciones de uso, renta de autos términos",
    canonical: "https://rivones.com/terminos",
  });

  return (
    <div className="bg-sand min-h-screen" data-testid="terminos-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-neutral-50 to-sand border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <p className="text-sm font-semibold tracking-[0.2em] text-terracotta uppercase">
            Documento legal
          </p>
          <h1
            className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-[#111111] tracking-tight mt-3"
            data-testid="terminos-h1"
          >
            Términos y Condiciones
          </h1>
          <p className="text-base sm:text-lg text-neutral-600 mt-4 leading-relaxed">
            Acuerdo legal vinculante entre usted y RivoNes que regula el acceso y uso de la
            Plataforma.
          </p>
          <p className="text-xs uppercase tracking-[0.18em] text-neutral-400 mt-6 font-semibold">
            Última actualización · Febrero 2026
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* TOC */}
        <nav
          aria-label="Índice"
          className="bg-white border border-neutral-200 rounded-2xl p-5 mb-10"
          data-testid="terminos-toc"
        >
          <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-3">
            Índice
          </p>
          <ol className="space-y-1.5 text-sm">
            {SECTIONS.map(({ id, n, title }) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  className="text-neutral-700 hover:text-terracotta transition-colors flex gap-2"
                >
                  <span className="text-neutral-400 tabular-nums">{n}.</span>
                  <span>{title}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-12">
          {SECTIONS.map(({ id, n, icon: Icon, title, body }) => (
            <article
              key={id}
              id={id}
              data-testid={`terminos-section-${id}`}
              className="scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex w-11 h-11 rounded-xl bg-neutral-100 items-center justify-center">
                  <Icon className="w-5 h-5 text-[#111111]" strokeWidth={2} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-neutral-400 font-semibold">
                    Sección {n}
                  </p>
                  <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#111111] leading-tight">
                    {title}
                  </h2>
                </div>
              </div>
              <div className="max-w-none text-neutral-700 leading-relaxed [&_p]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-3 [&_ul]:space-y-1 [&_li]:my-1 [&_strong]:text-[#111111] [&_strong]:font-semibold">
                {body}
              </div>
            </article>
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-14 p-5 rounded-2xl bg-neutral-100 border border-neutral-200 text-sm text-neutral-600">
          ¿Dudas sobre estos términos? Escríbenos desde la sección de{" "}
          <Link to="/profile" className="text-terracotta font-semibold hover:underline">
            Contacto
          </Link>{" "}
          o consulta también nuestra{" "}
          <Link to="/como-funciona" className="text-terracotta font-semibold hover:underline">
            guía Cómo funciona
          </Link>
          .
        </div>
      </section>
    </div>
  );
}
